import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import TitleCheckSvg from '../../../../../assets/images/views/lps/angeltax_type3/title-check.svg';
import ResultChevronSvg from '../../../../../assets/images/views/lps/angeltax_type3/result-chevron.svg';
import ResultNotchSvg from '../../../../../assets/images/views/lps/angeltax_type3/result-notch.svg';
import AngelTaxForm from '../../questions/angelTaxForm';
import { CALCULATION_NOTES, DISCLAIMER } from '../../questions/angelTaxNotes';
import {
  type AngelTaxResult,
  toDisplayYen,
} from '../../../models/questions/angelTaxCalculator';
import ResultCard from './resultCard';

// エンジェル税制 LP（angeltax_type3）に埋め込むシミュレーター。入力フォームはシミュレーターのページと共有し、
// 結果は LP のデザインに合わせた 3 列のカードで出す。注記の文面もシミュレーターのページと共有する。
// LP の強調色は angeltax_type3.scss の --angeltax-type3-accent を参照する。

export default function SimulatorView() {
  const [result, setResult] = useState<AngelTaxResult | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCalculate = (calculated: AngelTaxResult) => {
    // 結果を描画してからその位置へ送るため、state の反映を同期的に済ませる
    flushSync(() => {
      setResult(calculated);
    });
    resultRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="ui-root tw:mx-auto tw:w-full tw:max-w-275 tw:overflow-hidden tw:rounded-[8px] tw:border tw:border-(--angeltax-type3-accent) tw:bg-background tw:font-hiragino tw:text-foreground">
      <div className="tw:flex tw:flex-col tw:items-center tw:gap-10 tw:px-4 tw:pt-10 tw:pb-8 tw:md:px-6 tw:md:pt-15 tw:md:pb-10">
        <div className="tw:flex tw:flex-col tw:items-center tw:gap-2 tw:font-noto tw:font-bold tw:md:gap-4">
          <p className="tw:m-0 tw:flex tw:items-center tw:gap-2 tw:pb-1.5 tw:text-sm tw:leading-none tw:text-black tw:md:text-xl">
            <img
              src={TitleCheckSvg}
              width="28"
              height="28"
              alt=""
              className="tw:size-5 tw:md:size-7"
            />
            あなたの場合、いくら戻る？
          </p>
          <h2 className="tw:m-0 tw:border-b tw:border-(--angeltax-type3-accent) tw:pb-2 tw:text-center tw:text-2xl tw:leading-normal tw:font-bold tw:text-(--angeltax-type3-accent) tw:md:pb-3 tw:md:text-3xl tw:md:leading-none">
            エンジェル税制
            <br className="tw:md:hidden" />
            シミュレーター
          </h2>
        </div>
        <div className="tw:w-full tw:max-w-190">
          <AngelTaxForm
            calculated={result != null}
            onCalculate={handleCalculate}
          />
        </div>
      </div>

      {result != null && (
        <div
          ref={resultRef}
          className="tw:relative tw:flex tw:flex-col tw:items-center tw:gap-8 tw:bg-(--angeltax-type3-accent)/10 tw:px-4 tw:pt-15 tw:pb-6 tw:md:gap-10 tw:md:px-10 tw:md:pb-8"
        >
          {/* 上の白い面から続く切り欠き */}
          <img
            src={ResultNotchSvg}
            width="179"
            height="39"
            alt=""
            className="tw:absolute tw:-top-0.75 tw:left-1/2 tw:-translate-x-1/2 tw:rotate-180"
          />
          <div className="tw:flex tw:flex-col tw:items-center tw:gap-3">
            <h3 className="tw:m-0 tw:text-center tw:text-2xl tw:md:text-3xl tw:leading-none tw:font-bold tw:text-(--angeltax-type3-accent)">
              シミュレーション結果
            </h3>
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-1">
              <p className="tw:m-0 tw:font-noto tw:text-base tw:leading-[1.7] tw:font-medium tw:tracking-text tw:text-black tw:md:font-bold">
                エンジェル税制未使用の場合の所得税額
              </p>
              <p className="tw:m-0 tw:text-xl tw:leading-normal tw:font-bold tw:tracking-text tw:text-black">
                {toDisplayYen(result.incomeTax)}
              </p>
            </div>
            <img
              src={ResultChevronSvg}
              width="106"
              height="26"
              alt=""
              className="tw:rotate-180"
            />
          </div>

          <div className="tw:flex tw:w-full tw:flex-col tw:gap-4 tw:md:gap-6">
            {/* 見出しの pill がカードの上へはみ出すため、縦積みの SP ではカード間を広めに取る */}
            <div className="tw:flex tw:flex-col tw:gap-10 tw:lg:flex-row tw:lg:items-stretch tw:lg:gap-5">
              <ResultCard
                tone="a"
                title="優遇措置A"
                angelTax={result.angelTaxA}
              />
              <ResultCard
                tone="b"
                title="優遇措置B"
                angelTax={result.angelTaxB}
              />
              <ResultCard
                tone="pre-seed"
                title="プレシード・シード特例"
                angelTax={result.angelTaxPreSeed}
              />
            </div>
            <div className="tw:flex tw:flex-col tw:gap-3 tw:text-xs tw:leading-[1.4] tw:md:px-8 tw:md:leading-normal tw:text-(--angeltax-type3-accent)">
              <div className="tw:flex tw:flex-col tw:gap-2 tw:border-b tw:border-(--angeltax-type3-accent) tw:pb-3">
                <p className="tw:m-0 tw:text-sm tw:leading-none">
                  所得税の計算について
                </p>
                <ul className="tw:m-0 tw:list-disc tw:ps-4.5">
                  {CALCULATION_NOTES.map(note => (
                    <li key={note}>{note}</li>
                  ))}
                </ul>
              </div>
              <p className="tw:m-0">{DISCLAIMER}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
